<template>
  <ContextColorProvider context="brand">
    <div class="home-view">
      <HomeTemplate>
        <template #main-background>
          <HomeIllustration />
        </template>
        <template #main>
          <HomeAccountStatus
            v-if="accountStatusResponse?.accountStatus"
            :account-status-response="accountStatusResponse"
          />
          <HomeSuitability
            v-else-if="
              accountStatusResponse &&
              !accountStatusResponse.hasInvestmentProfile
            "
          />
          <HomeBalanceSummary
            v-else
            :has-error="hasError"
            :balance-details="balanceDetails"
            :should-use-new-balances-route="shouldUseNewBalancesRoute"
            :loading="isLoading"
            :is-overdue="isOverdue"
            @on-retry="reloadBalanceSummary"
          />
        </template>
        <template #content>
          <HomeActivities
            @open-register-panel="toogleOpenRegisterPanel"
            @open-orders-panel="openPendingOrders"
          />
          <HomeWidgets :show-investments-widgets="showInvestmentsWidgets" />
          <HomeNews />
        </template>
      </HomeTemplate>
    </div>
    <HomeRegister
      :is-open="openRegisterPanel"
      @close="toogleOpenRegisterPanel"
    />
    <PendingOrdersSheet
      :is-open="isPendingOrdersOpen"
      :order-type="pendingOrdersType"
      @close="isPendingOrdersOpen = false"
    />
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, ProvideReactive, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ContextColorProvider } from '@warrenbrasil/nebraska-web';
import { HomeTemplate } from '@/modules/home/components/HomeTemplate';
import { HomeIllustration } from '@/modules/home/components/HomeIllustration';
import { HomeNews } from '@/modules/home/components/HomeNews';
import { HomeWidgets } from '@/modules/home/components/HomeWidgets';
import { HomeRegister } from '@/modules/home/components/HomeRegister';
import { HomeActivities } from '@/modules/home/components/HomeActivities';
import { clearNextRoutes } from '@/modules/common/store/RegulatoryUpdateRoutesOrchestrator';
import { UIModule } from '@/modules/common/store/UIModule';
import * as trackEvents from './track-events';

import {
  BalancesResponse,
  IBalancesImages,
  TotalBalanceResponse,
  getBalances,
  getTotalBalance
} from '@/modules/home/services/bff';
import HomeBalanceSummary from '@/modules/home/components/HomeBalanceSummary/HomeBalanceSummary.vue';
import HomeAccountStatus from '@/modules/home/components/HomeAccountStatus/HomeAccountStatus.vue';
import HomeSuitability from '@/modules/home/components/HomeSuitability/HomeSuitability.vue';
import { PendingOrdersSheet } from '@/modules/home/components/PendingOrdersSheet';
import { AccountStates } from '@/modules/common/services/http/account';
import { ICustomer } from '@/types/models';
import { EButtonTypeV2 } from '@/modules/home/services/consolidation';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';

const CustomerModule = namespace('CustomerModule');

@Component({
  components: {
    ContextColorProvider,
    HomeTemplate,
    HomeIllustration,
    HomeNews,
    HomeWidgets,
    HomeBalanceSummary,
    HomeRegister,
    HomeAccountStatus,
    HomeSuitability,
    PendingOrdersSheet,
    HomeActivities
  }
})
export default class Home extends Vue {
  balanceDetails: Reactive<TotalBalanceResponse | BalancesResponse> = null;
  images: Reactive<IBalancesImages> = null;

  isLoading = false;
  hasError = false;
  openRegisterPanel = false;
  isPendingOrdersOpen = false;
  pendingOrdersType = EButtonTypeV2.INVESTMENT;

  @CustomerModule.Getter('isRegulatoryStatusBlocked')
  readonly isOverdue!: boolean;

  @CustomerModule.Getter('getCustomerId')
  readonly customerApiId!: string;

  @CustomerModule.Getter('getCustomer')
  readonly customer!: ICustomer;

  @CustomerModule.Getter('getAccountStatus')
  readonly accountStatusResponse!: AccountStates;

  @ProvideReactive()
  get shouldTriggerHomeAnimations() {
    return !UIModule.getIsOpenWelcomeCurtain;
  }

  get showInvestmentsWidgets() {
    return this.accountStatusResponse?.clientEnabled ?? false;
  }

  get shouldUseNewBalancesRoute() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_BFF_BANKING_MIGRATION
    );
  }

  openPendingOrders(type: EButtonTypeV2) {
    this.pendingOrdersType = type;
    this.isPendingOrdersOpen = true;
  }

  created() {
    this.load();
  }

  mounted() {
    trackEvents.trackHomePageView();
    clearNextRoutes();
  }

  reloadBalanceSummary() {
    this.load();
    trackEvents.trackReloadWorthErrorClick();
  }

  toogleOpenRegisterPanel() {
    this.openRegisterPanel = !this.openRegisterPanel;
  }

  async load() {
    this.isLoading = true;
    this.hasError = false;

    try {
      await this.loadHeroBalance();
    } catch {
      this.hasError = true;
      trackEvents.trackWorthErrorLoad();
    } finally {
      this.isLoading = false;
    }
  }

  private async loadHeroBalance() {
    if (this.shouldUseNewBalancesRoute) {
      this.balanceDetails = await getTotalBalance();
    } else {
      this.balanceDetails = await getBalances();
    }
  }
}
</script>
